import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { DisplayTypeEnum } from '../task-web-properties/task-web-properties.component';
import { IncomingCasesTaskViewModel } from '@supoui/shared/components/task-web-modal/models/incoming-cases-task-view-model';
import { CauseItemType } from '@supoui/shared/model/cause-item-type';
import { PurposeOfTravelItemType } from '@supoui/shared/model/purpose-of-travel-item-type';
import { SimpleCaseStakeholder } from '@supoui/shared/model/simpleCaseStakeholder';
import { StakeholderTypeEnum } from '@supoui/shared/model/enumerations/stakeholder-type.enum';
import { CaseService } from '@supoui/core/services/case.service';
import { MasterListService } from '@supoui/shared/components/task-web-modal/services/masterlist-service';
import { ServiceItemType } from '@supoui/shared/model/service-item-type';
import { IApplicationCustomerProfile } from '@supoui/shared/model/application-customer-profile';
import { TaskType } from '@supoui/shared/model/enumerations/task-enums';
import { UntilDestroy } from '@ngneat/until-destroy';

@Component({
    selector: 'app-task-web-details-panel',
    templateUrl: './task-web-details-panel.component.html',
    styleUrls: ['./task-web-details-panel.component.scss'],
    standalone: false
})
@UntilDestroy({ checkProperties: true })
export class TaskWebDetailsPanelComponent implements OnInit, OnChanges {
  @Input() taskDataViewModel: IncomingCasesTaskViewModel;

  propertiesDisplayTypes = { incidentDescription: DisplayTypeEnum.FORMATTED_TEXT, remarks: DisplayTypeEnum.FORMATTED_TEXT };

  // Ignored properties
  ignoredProperties = ['id', 'properties', 'stakeholderType', 'nationalIdValid', 'isCompany'];
  travelIgnoredProperties = ['luggage'];
  ignoredPropertiesByName = { travel: this.travelIgnoredProperties };

  // Hidden properties
  incidentHiddenProperties = [
    'streetNumber',
    'streetName',
    'locality',
    'administrativeAreaLevel1',
    'administrativeAreaLevel2',
    'country',
    'postalCode',
    'latitude',
    'longitude',
    'countryCodeAlpha2',
    'timeZoneId',
    'utcOffsetInHours'
  ];

  travelHiddenProperties = [
    'streetNumber',
    'streetName',
    'locality',
    'administrativeAreaLevel1',
    'administrativeAreaLevel2',
    'country',
    'postalCode',
    'latitude',
    'longitude',
    'countryCodeAlpha2',
    'timeZoneId',
    'utcOffsetInHours'
  ];

  nameToHiddenPropertiesMap = { incident: this.incidentHiddenProperties, travel: this.travelHiddenProperties };
  hiddenPropertiesByName = { ...this.nameToHiddenPropertiesMap };

  endUser: SimpleCaseStakeholder;
  reporter: SimpleCaseStakeholder;
  policyHolder: SimpleCaseStakeholder;
  internalSummary: string;
  reporterIsEndUser = false;
  reporterIsPolicyHolder = false;

  @Input() serviceItemList: ServiceItemType[];
  @Input() purposeOfTravelItemList: PurposeOfTravelItemType[];
  @Input() causes: Map<string, CauseItemType[]>;
  @Input() customerProfiles: IApplicationCustomerProfile[];

  // serviceItemList: ServiceItemType[];
  // purposeOfTravelItemList: PurposeOfTravelItemType[];
  // causeItemList: CauseItemType[];
  // customerProfiles: CustomerProfiles[];

  public readonly TaskType = TaskType;

  constructor(private caseService: CaseService, private masterListService: MasterListService) {
    // this.masterListService.getServiceItemList().subscribe(result => (this.serviceItemList = result));
    // this.masterListService.getPurposeOfTravelList().subscribe(result => (this.purposeOfTravelItemList = result));
    // this.masterListService.getAllLevel1Causes().subscribe(result => (this.causeItemList = result));
    // this.caseService.getCustomerProfiles().subscribe(result => (this.customerProfiles = result));
  }

  ngOnInit(): void {
    if (!this.serviceItemList) {
      this.masterListService.getServiceItemList().subscribe(result => (this.serviceItemList = result));
    }
    if (!this.purposeOfTravelItemList) {
      this.masterListService.getPurposeOfTravelList().subscribe(result => (this.purposeOfTravelItemList = result));
    }
    if (!this.causes) {
      this.causes = new Map<string, CauseItemType[]>();
      this.masterListService.getAllLevel1Causes().subscribe(result => this.causes.set('level1', result));
      this.masterListService.getAllLevel2Causes().subscribe(result => this.causes.set('level2', result));
      this.masterListService.getAllLevel3Causes().subscribe(result => this.causes.set('level3', result));
    }
    if (!this.customerProfiles) {
      this.caseService.getCustomerTravelProfiles().subscribe(result => (this.customerProfiles = result));
    }
  }

  ngOnChanges(): void {
    if (this.taskDataViewModel) {
      this.getRelevantInformation();
    }
  }

  showMore(name: string): void {
    this.hiddenPropertiesByName[name] = null;
  }

  showLess(name: string): void {
    const ignoredProperties = this.nameToHiddenPropertiesMap[name];
    this.hiddenPropertiesByName[name] = ignoredProperties;
  }

  hasHiddenProperties(name: string): boolean {
    return !!this.hiddenPropertiesByName[name];
  }

  hasNonNullHiddenProperty(name: string, ...obj: any): boolean {
    return (
      !!this.hiddenPropertiesByName[name] &&
      !!this.hiddenPropertiesByName[name]?.find((prop: any) => obj && obj.find((o: any) => o && o[prop]))
    );
  }

  getHiddenProperties(name: string): string[] {
    return this.hiddenPropertiesByName[name];
  }

  getIgnoredProperties(name: string): string[] {
    const ignoredProperties = this.ignoredPropertiesByName[name] || [];
    const hiddenProperties = this.hiddenPropertiesByName[name] || [];

    const ignored = [...ignoredProperties, ...hiddenProperties];
    return ignored;
  }

  private getRelevantInformation(): void {
    this.endUser = this.taskDataViewModel?.data?.caseStakeholders?.find(
      (stakeHolder: any) => stakeHolder.stakeholderType === StakeholderTypeEnum.endUser
    );
    this.reporter = this.taskDataViewModel?.data?.caseStakeholders?.find(
      (stakeHolder: any) => stakeHolder.stakeholderType === StakeholderTypeEnum.reporter
    );
    this.policyHolder = this.taskDataViewModel?.data?.caseStakeholders?.find(
      (stakeHolder: any) => stakeHolder.stakeholderType === StakeholderTypeEnum.policyHolder
    );
    this.internalSummary = this.taskDataViewModel?.internalSummary;

    if (!this.endUser) {
      if (this.reporter?.properties?.includes(StakeholderTypeEnum.endUser)) {
        this.endUser = this.reporter;
        this.reporterIsEndUser = true;
      }
    }

    if (!this.policyHolder) {
      if (this.reporter?.properties?.includes(StakeholderTypeEnum.policyHolder)) {
        this.policyHolder = this.reporter;
        this.reporterIsPolicyHolder = true;
      }
    }
  }
}
